import React, { useMemo } from 'react';

import { ActionIcon, Badge, Divider, Flex, Popover, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconBraces, IconVariable } from '@tabler/icons-react';
import { TYPE_COLORS } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/constants';
import { SchemaUtils } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/utils';
import { useTranslation } from 'react-i18next';

interface VariableInsertButtonProps {
  jsonSchema: string;
  onInsertVariable: (variable: string) => void;
}

export function VariableInsertButton({ jsonSchema, onInsertVariable }: VariableInsertButtonProps) {
  const { t } = useTranslation();

  const variableProperties = useMemo(() => {
    if (!jsonSchema) return {};
    const parsedSchema = SchemaUtils.parseJsonString(jsonSchema);
    return parsedSchema ? SchemaUtils.getVariableProperties(parsedSchema) : {};
  }, [jsonSchema]);

  const variables = Object.entries(variableProperties);

  const getAbbreviatedPath = (path: string) => {
    return path.length > 20 ? `${path.substring(0, 20)}...` : path;
  };

  return (
    <Popover width={320} position="bottom-end" withArrow shadow="md" offset={8} withinPortal>
      <Popover.Target>
        <ActionIcon variant="subtle" color="gray" size="sm" aria-label={t('VARIABLE_INSERT_BUTTON.INSERT_VARIABLE')}>
          <IconBraces style={{ height: 18, width: 18 }} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown py="xs" px={0}>
        <Flex px="md" gap={4} justify="center">
          <IconVariable style={{ height: 16, width: 16 }} />
          <Text size="sm" fw={500} c="dark" mb="xs">
            {t('VARIABLE_INSERT_BUTTON.INSERT_VARIABLE')}
          </Text>
        </Flex>

        <Divider />

        {variables.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" py="md">
            {t('VARIABLE_INSERT_BUTTON.NO_VARIABLES_AVAILABLE')}
          </Text>
        ) : (
          <ScrollArea.Autosize mah={200}>
            <Stack spacing={4} p="xs">
              {variables.map(([path, property]) => (
                <UnstyledButton
                  key={path}
                  onClick={() => onInsertVariable(path)}
                  p="xs"
                  w="100%"
                  style={{ borderRadius: 4 }}>
                  <Flex align="center" gap="xs">
                    <Text
                      size="xs"
                      ff="monospace"
                      c="dark"
                      fw={500}
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {`{{${getAbbreviatedPath(path)}}}`}
                    </Text>
                    <Badge
                      size="sm"
                      variant="light"
                      tt="none"
                      fw={500}
                      color={TYPE_COLORS[property.type] || 'gray'}
                      style={{ flexShrink: 0 }}>
                      {property.type}
                    </Badge>
                  </Flex>
                  {property.description && (
                    <Text size="xs" c="dimmed" mt={2} style={{ textAlign: 'left' }}>
                      {property.description}
                    </Text>
                  )}
                </UnstyledButton>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
